import React from "react";

/**
 * Hand-drawn GDIOT-style hero decoration.
 * Subtle lavender doodles, an organic halo behind the founder and
 * dotted paths that gently point Student -> Founder <- Teacher.
 * Purely decorative (aria-hidden, pointer-events-none).
 */
export default function HeroDecor() {
  const stroke = { fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
      {/* Organic lavender halo behind the founder */}
      <div className="absolute left-1/2 top-[62%] -translate-x-1/2 -translate-y-1/2 h-[26rem] w-[60rem] max-w-[94%] rounded-full bg-violet-200/50 blur-[70px]" />
      <div className="absolute left-1/2 top-[58%] -translate-x-1/2 -translate-y-1/2 h-[18rem] w-[18rem] rounded-full bg-fuchsia-200/40 blur-[70px]" />
      <svg className="absolute left-1/2 top-[62%] -translate-x-1/2 -translate-y-1/2 w-[74%] max-w-4xl opacity-70" viewBox="0 0 640 420" fill="none">
        <path d="M120,210 C90,120 220,60 330,80 C470,105 600,150 560,255 C525,345 400,382 280,360 C165,340 150,300 120,210 Z" fill="#ECE8FE" />
      </svg>

      {/* ---- Doodles (thin lavender outlines, low opacity) ---- */}
      {/* lightbulb (top-left) */}
      <svg className="absolute top-[9%] left-[8%] h-9 w-9 text-violet-400 opacity-55" viewBox="0 0 26 26" stroke="currentColor" strokeWidth="1.4" style={stroke}>
        <path d="M10 20h6M11 23h4M13 3a7 7 0 0 0-5 11.6c.9.9 1.2 1.6 1.3 2.4h7.4c.1-.8.4-1.5 1.3-2.4A7 7 0 0 0 13 3Z" />
      </svg>
      {/* 4-point star (top, near heading) */}
      <svg className="absolute top-[5%] left-[26%] h-6 w-6 text-violet-300 opacity-70" viewBox="0 0 32 32" stroke="currentColor" strokeWidth="1.4" style={stroke}>
        <path d="M16 2c1 8 6 13 14 14-8 1-13 6-14 14-1-8-6-13-14-14C10 15 15 10 16 2Z" />
      </svg>
      {/* plus symbol */}
      <svg className="absolute top-[15%] left-[17%] h-5 w-5 text-violet-300 opacity-60" viewBox="0 0 28 28" stroke="currentColor" strokeWidth="1.6" style={stroke}>
        <path d="M6 14h16M14 6v16" />
      </svg>
      {/* paper plane (top-right) */}
      <svg className="absolute top-[7%] right-[12%] h-9 w-9 text-violet-400 opacity-55 rotate-6" viewBox="0 0 33 33" stroke="currentColor" strokeWidth="1.4" style={stroke}>
        <path d="M30 3 3 14l10 3 3 10L30 3Z" />
        <path d="M13 17 30 3" />
      </svg>
      {/* puzzle piece (top-right) */}
      <svg className="absolute top-[16%] right-[7%] h-10 w-10 text-violet-300 opacity-60 rotate-6" viewBox="0 0 26 26" stroke="currentColor" strokeWidth="1.4" style={stroke}>
        <path d="M4 9h3.2a2 2 0 1 1 3.6 0H14v3.2a2 2 0 1 1 0 3.6V19H10.8a2 2 0 1 0-3.6 0H4v-3.2a2 2 0 1 0 0-3.6Z" />
      </svg>
      {/* times symbol (right) */}
      <svg className="absolute top-[26%] right-[18%] h-4 w-4 text-amber-400 opacity-55" viewBox="0 0 28 28" stroke="currentColor" strokeWidth="1.6" style={stroke}>
        <path d="M7 7 21 21M21 7 7 21" />
      </svg>
      {/* squiggle (left) */}
      <svg className="absolute top-[40%] left-[5%] h-8 w-16 text-violet-300 opacity-60" viewBox="0 0 60 24" stroke="currentColor" strokeWidth="1.6" style={stroke}>
        <path d="M2 12C8 2 14 22 20 12S34 2 40 12s14 8 18 0" />
      </svg>
      {/* small circle (right) */}
      <svg className="absolute top-[50%] right-[6%] h-6 w-6 text-violet-300 opacity-55" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.4" style={stroke}>
        <circle cx="12" cy="12" r="8" />
      </svg>
      {/* spiral (left, lower) */}
      <svg className="absolute top-[64%] left-[9%] h-8 w-8 text-violet-300 opacity-55" viewBox="0 0 30 30" stroke="currentColor" strokeWidth="1.4" style={stroke}>
        <path d="M15 15a2 2 0 1 0 2 2 5 5 0 1 0-5-5 8 8 0 1 0 8 8" />
      </svg>
      {/* equals symbol (right, lower) */}
      <svg className="absolute top-[70%] right-[12%] h-4 w-6 text-violet-300 opacity-55" viewBox="0 0 28 20" stroke="currentColor" strokeWidth="1.6" style={stroke}>
        <path d="M6 7h16M6 14h16" />
      </svg>
      {/* tiny stars */}
      <svg className="absolute top-[46%] right-[24%] h-4 w-4 text-amber-400 opacity-55" viewBox="0 0 32 32" stroke="currentColor" strokeWidth="1.6" style={stroke}>
        <path d="M16 3c.8 7 5 11 12 12-7 1-11 5-12 12-.8-7-5-11-12-12C11 14 15 10 16 3Z" />
      </svg>
      <svg className="absolute top-[72%] left-[22%] h-5 w-5 text-violet-300 opacity-60" viewBox="0 0 32 32" stroke="currentColor" strokeWidth="1.4" style={stroke}>
        <path d="M16 3c.8 7 5 11 12 12-7 1-11 5-12 12-.8-7-5-11-12-12C11 14 15 10 16 3Z" />
      </svg>

      {/* Dotted curved paths pointing inward toward the founder */}
      {/* Student side (left -> right) */}
      <svg className="absolute top-[60%] left-[16%] h-14 w-40 text-violet-400 opacity-45" viewBox="0 0 160 70" stroke="currentColor" strokeWidth="2" fill="none">
        <path d="M6 58 C 40 12, 110 8, 150 34" strokeDasharray="1.5 8" strokeLinecap="round" />
        <path d="M139 24 152 34 137 41" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {/* Teacher side (right -> left) */}
      <svg className="absolute top-[60%] right-[16%] h-14 w-40 text-amber-400 opacity-45" viewBox="0 0 160 70" stroke="currentColor" strokeWidth="2" fill="none">
        <path d="M154 58 C 120 12, 50 8, 10 34" strokeDasharray="1.5 8" strokeLinecap="round" />
        <path d="M21 24 8 34 23 41" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}
